import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import './Auth.css';

const Register = () => {
    const [step, setStep] = useState('register'); // 'register' or 'verify'
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        password: '',
        confirmPassword: '',
        dob: '',
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });
    const [otp, setOtp] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);
    const { login, updateUser } = useAuth();
    const navigate = useNavigate();

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (step === 'register') {
            if (formData.password !== formData.confirmPassword) {
                setError('Passwords do not match');
                return;
            }

            if (formData.password.length < 8) {
                setError('Password must be at least 8 characters');
                return;
            }

            setLoading(true);

            try {
                const { confirmPassword, ...registerData } = formData;
                await api.post('/auth/register', registerData);
                setStep('verify');
            } catch (err) {
                setError(err.response?.data?.message || 'Registration failed. Please try again.');
            } finally {
                setLoading(false);
            }
        } else if (step === 'verify') {
            if (!otp || otp.length !== 6) {
                setError('Please enter a valid 6-digit OTP');
                return;
            }

            setLoading(true);

            try {
                const response = await api.post('/auth/verify-email-otp', {
                    email: formData.email,
                    otp
                });

                // Use the tokens returned from verification
                const { accessToken, refreshToken, user } = response.data;
                localStorage.setItem('accessToken', accessToken);
                localStorage.setItem('refreshToken', refreshToken);

                // Update auth context
                updateUser(user);

                navigate('/dashboard');
            } catch (err) {
                setError(err.response?.data?.message || 'Verification failed. Please try again.');
            } finally {
                setLoading(false);
            }
        }
    };

    const handleResendOTP = async () => {
        setResendLoading(true);
        setError('');

        try {
            await api.post('/auth/resend-otp', { email: formData.email });
            setError('OTP sent successfully. Check your email.');
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to resend OTP. Please try again.');
        } finally {
            setResendLoading(false);
        }
    };

    return (
        <div className="auth-container">
            <div className="auth-card">
                <h1 className="auth-title">Dr. Within Health Journal</h1>
                <h2 className="auth-subtitle">
                    {step === 'register' ? 'Create Account' : 'Verify Email'}
                </h2>

                {error && (
                    <div className={`alert ${error.includes('successfully') ? 'alert-success' : 'alert-error'}`}>
                        {error}
                    </div>
                )}

                {step === 'register' ? (
                    <form onSubmit={handleSubmit}>
                        <div className="form-group">
                            <label>Full Name</label>
                            <input
                                type="text"
                                name="fullName"
                                value={formData.fullName}
                                onChange={handleChange}
                                placeholder="Enter full name"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Email</label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="Enter email"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Date of Birth</label>
                            <input
                                type="date"
                                name="dob"
                                value={formData.dob}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="form-group">
                            <label>Password</label>
                            <input
                                type="password"
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                placeholder="••••••••"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Confirm Password</label>
                            <input
                                type="password"
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                placeholder="••••••••"
                                required
                            />
                        </div>

                        <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
                            {loading ? <div className="spinner"></div> : 'Create Account'}
                        </button>
                    </form>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <p className="auth-description">
                            We've sent a 6-digit OTP to <strong>{formData.email}</strong>.
                            Please enter it below to verify your email.
                        </p>

                        <div className="form-group">
                            <label>OTP Code</label>
                            <input
                                type="text"
                                value={otp}
                                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                placeholder="123456"
                                maxLength="6"
                                required
                                style={{ textAlign: 'center', fontSize: '18px', letterSpacing: '5px' }}
                            />
                        </div>

                        <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
                            {loading ? <div className="spinner"></div> : 'Verify Email'}
                        </button>

                        <div style={{ textAlign: 'center', marginTop: '16px' }}>
                            <button
                                type="button"
                                className="btn btn-link"
                                onClick={handleResendOTP}
                                disabled={resendLoading}
                            >
                                {resendLoading ? 'Sending...' : 'Resend OTP'}
                            </button>
                        </div>
                    </form>
                )}

                <div className="auth-footer">
                    <p>Already have an account? <Link to="/login" className="auth-link">Sign in</Link></p>
                </div>
            </div>
        </div>
    );
};

export default Register;
